import { Card, CardContent, CardHeader, Grid, Stack } from "@mui/material";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import SwipeableViews from "react-swipeable-views";
import { Stepper } from "@common/Stepper/Stepper";
import { EPAYMENT_TYPES, TITLE_PRIMARY_STYLE } from "@constants";
import { EPaymentIframes } from "../interface/IPayment";
import "./css/PaymentCardList.scss";
import { usePaymentCardList } from "./hook/usePaymentCardList";
import { PaymentCardAction } from "./PaymentCardAction";
import { PaymentCardItem } from "./PaymentCardItem";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { ElavonPaymentIframe } from "@common/PaymentIframe/elavon/ElavonPaymentIframe";
import { CARDS_HPP_API_ROUTE } from "@api-routes/eirStancerFacade";
import { CARDS_HPP_API_ROUTE as ELAVON_CARDS_HPP_API_ROUTE } from "@api-routes/elavonFacade";
import { StancerPaymentIframe } from "@common/PaymentIframe";
import { IError, IFetchState } from "itsf-ui-common";
import { IPaymentMethod } from "@features/Payment/interface/IPaymentMethod";

interface IParams {
    openedIframe?: EPaymentIframes;
    setOpenedIframe(openedIframe?: EPaymentIframes): void;
    accountId: string;
    isNewPaymentProvider: boolean;
    paymentMethodList: IPaymentMethod[];
    getPayerState: IFetchState;
    successCallback(): void;
    errorCallback?(error: IError): void;
}

export const PaymentCardList = (props: IParams) => {
    const {
        openedIframe,
        setOpenedIframe,
        accountId,
        isNewPaymentProvider,
        paymentMethodList,
        getPayerState,
        successCallback,
        errorCallback,
    } = props;
    const { cardState, setDefaultCard, removeCard, retryCard } = usePaymentCardList({
        accountId,
        successCallback,
        errorCallback,
    });

    const [arrowStep, setArrowStep] = useState(0);
    const { t } = useTranslation("payment");

    const handleStepChange = (step: number) => {
        setArrowStep(step);
    };

    const loadIframe = () => {
        setOpenedIframe(EPaymentIframes.ADD_CARD);
    };

    const closeIframe = () => {
        setOpenedIframe();
    };

    const creditCards = paymentMethodList.filter(
        (paymentMethod) => paymentMethod.paymentMethodType === EPAYMENT_TYPES.CREDIT_CARD
    );

    return (
        <Card elevation={2}>
            <CardHeader title={t("manageCards")} titleTypographyProps={TITLE_PRIMARY_STYLE} />
            <HandleErrorAndLoading
                error={getPayerState.error ?? cardState.error}
                isLoading={getPayerState.isLoading || cardState.isLoading}
                retryFetch={retryCard}
            >
                <CardContent className="payment-content">
                    {creditCards && creditCards.length > 0 && (
                        <Fragment>
                            <SwipeableViews
                                enableMouseEvents
                                index={arrowStep}
                                slideStyle={{ display: "flex", justifyContent: "center", overflow: "hidden" }}
                                style={{ display: "flex", justifyContent: "center" }}
                                onChangeIndex={handleStepChange}
                            >
                                {creditCards.map((card) => (
                                    <PaymentCardItem
                                        accountId={accountId}
                                        card={card}
                                        closeIframe={closeIframe}
                                        isIframeDisplayed={openedIframe === EPaymentIframes.ADD_CARD}
                                        key={card.paymentMethodId}
                                        removeCard={removeCard}
                                        setDefaultCard={setDefaultCard}
                                    />
                                ))}
                            </SwipeableViews>
                            <Grid alignItems="center" container justifyContent="center">
                                <Grid item xs={6}>
                                    <Stepper
                                        activeStep={arrowStep}
                                        fontSizeArrow="large"
                                        handleStepChange={handleStepChange}
                                        stepsLength={creditCards.length}
                                    />
                                </Grid>
                            </Grid>
                        </Fragment>
                    )}
                    {openedIframe === EPaymentIframes.ADD_CARD && (
                        <Stack className="payment-add">
                            <div className="payment-wrapper">
                                {isNewPaymentProvider ? (
                                    <StancerPaymentIframe
                                        iframePaymentApiRoute={CARDS_HPP_API_ROUTE}
                                        successCallback={successCallback}
                                        validatePaymentApiRoute={CARDS_HPP_API_ROUTE}
                                    />
                                ) : (
                                    <ElavonPaymentIframe
                                        iframePaymentApiRoute={ELAVON_CARDS_HPP_API_ROUTE}
                                        successCallback={successCallback}
                                        validatePaymentApiRoute={ELAVON_CARDS_HPP_API_ROUTE}
                                    />
                                )}
                            </div>
                        </Stack>
                    )}
                </CardContent>
                <PaymentCardAction
                    closeIframe={closeIframe}
                    isIframeDisplayed={openedIframe === EPaymentIframes.ADD_CARD}
                    loadIframe={loadIframe}
                />
            </HandleErrorAndLoading>
        </Card>
    );
};
